// ─────────────────────────────────────────
//  AUTH FUNCTIONS
// ─────────────────────────────────────────

import { S, sb, ls, setLs, rmLs } from './state.js';
import { showToast } from './utils.js';
import { preloadBestExamples } from './prompts.js';
import { showFullStackButton } from './fullstack.js';
import { closeSettings } from './settings.js';
import { loadActiveConversation } from './conversations.js';

export function switchAuthTab(tab) {
  const tabSignup = document.getElementById('tab-signup');
  const tabLogin = document.getElementById('tab-login');
  const formSignup = document.getElementById('auth-form-signup');
  const formLogin = document.getElementById('auth-form-login');
  const authError = document.getElementById('auth-error');

  if (tabSignup) tabSignup.classList.toggle('active', tab === 'signup');
  if (tabLogin) tabLogin.classList.toggle('active', tab === 'login');
  if (formSignup) formSignup.style.display = tab === 'signup' ? 'block' : 'none';
  if (formLogin) formLogin.style.display = tab === 'login' ? 'block' : 'none';
  if (authError) authError.classList.remove('show');
}

export function showAuthError(msg) {
  const el = document.getElementById('auth-error');
  if (!el) return;
  el.textContent = msg;
  el.classList.add('show');
}

export function authSignUp() {
  const nameEl = document.getElementById('auth-name');
  const emailEl = document.getElementById('auth-email-signup');
  const passEl = document.getElementById('auth-pass-signup');
  const name = nameEl?.value.trim() || '';
  const email = emailEl?.value.trim() || '';
  const pass = passEl?.value || '';

  if (!name) { showAuthError('Please enter your name.'); return; }
  if (!email) { showAuthError('Please enter your email.'); return; }
  if (pass.length < 6) { showAuthError('Password must be at least 6 characters.'); return; }

  const btn = document.getElementById('auth-submit-signup');
  if (btn) { btn.disabled = true; btn.textContent = 'Creating account...'; }

  sb.auth.signUp({
    email,
    password: pass,
    options: { data: { name } }
  }).then(({ data, error }) => {
    if (btn) { btn.disabled = false; btn.textContent = 'Create Account →'; }

    if (error) { showAuthError(error.message); return; }

    // Some Supabase configs require email confirmation
    if (data.user && !data.session) {
      showAuthError('Check your email for a confirmation link, then log in.');
      switchAuthTab('login');
      return;
    }

    if (data.session) {
      onAuthSuccess(data.session);
    }
  }).catch(() => {
    if (btn) { btn.disabled = false; btn.textContent = 'Create Account →'; }
    showAuthError('Connection error — please try again.');
  });
}

export function authLogIn() {
  const emailEl = document.getElementById('auth-email-login');
  const passEl = document.getElementById('auth-pass-login');
  const email = emailEl?.value.trim() || '';
  const pass = passEl?.value || '';

  if (!email || !pass) { showAuthError('Please enter email and password.'); return; }

  const btn = document.getElementById('auth-submit-login');
  if (btn) { btn.disabled = true; btn.textContent = 'Logging in...'; }

  sb.auth.signInWithPassword({ email, password: pass })
    .then(({ data, error }) => {
      if (btn) { btn.disabled = false; btn.textContent = 'Log In →'; }

      if (error) { showAuthError(error.message); return; }
      if (data.session) {
        onAuthSuccess(data.session);
      }
    })
    .catch(() => {
      if (btn) { btn.disabled = false; btn.textContent = 'Log In →'; }
      showAuthError('Connection error — please try again.');
    });
}

export function authGoogle() {
  sb.auth.signInWithOAuth({
    provider: 'google',
    options: {
      redirectTo: window.location.origin + '/studio'
    }
  }).then(({ error }) => {
    if (error) { showAuthError(error.message); }
  }).catch(() => {
    showAuthError('Connection error — please try again.');
  });
}

export function authResetPassword() {
  const emailEl = document.getElementById('auth-email-login');
  const email = emailEl?.value.trim() || '';
  if (!email) { showAuthError('Enter your email above first.'); return; }

  sb.auth.resetPasswordForEmail(email, {
    redirectTo: window.location.origin + '/studio'
  }).then(({ error }) => {
    if (error) { showAuthError(error.message); return; }
    const el = document.getElementById('auth-error');
    if (el) {
      el.textContent = 'Password reset link sent! Check your email.';
      el.classList.add('show');
      el.style.borderColor = 'var(--green)';
      el.style.color = 'var(--green)';
      el.style.background = 'var(--green-dim)';
    }
  }).catch(() => {
    showAuthError('Connection error — please try again.');
  });
}

export function authSignOut() {
  // Sign out from Supabase (fire and forget)
  sb.auth.signOut().then(() => {}).catch(() => {});

  // Clear local state immediately
  closeSettings();
  S.session = null;
  S.user = null;
  S.name = '';
  S.key = '';
  S.tier = 'free';
  S.isAdmin = false;
  S.history = [];
  S.code = '';
  S.convId = null;
  S.projectId = null;
  S.localProjectId = null;

  // Clear localStorage
  rmLs('vc_name');
  rmLs('vc_key');

  // Reset UI
  const authScreen = document.getElementById('auth-screen');
  if (authScreen) authScreen.classList.remove('hidden');
  const userPill = document.getElementById('user-pill');
  if (userPill) userPill.style.display = 'none';
  showToast('Signed out');

  // Reload to get a clean state
  setTimeout(() => window.location.reload(), 500);
}

export function onAuthSuccess(session) {
  S.session = session;
  const authScreen = document.getElementById('auth-screen');
  if (authScreen) authScreen.classList.add('hidden');

  // Show user pill immediately with email fallback
  const quickName = session.user.user_metadata?.name || session.user.user_metadata?.full_name || session.user.email?.split('@')[0] || 'Builder';
  S.name = quickName;
  setLs('vc_name', quickName);
  const pillName = document.getElementById('user-pill-name');
  if (pillName) pillName.textContent = quickName;
  const userPill = document.getElementById('user-pill');
  if (userPill) userPill.style.display = 'flex';

  // Personalize greeting immediately
  const tgGreet = document.getElementById('tg-greeting');
  if (tgGreet) tgGreet.textContent = `Hey ${quickName}! What will you build today?`;

  // Load profile in background (non-blocking) to get tier + admin status
  sb.from('users')
    .select('name, tier, cohort_id, is_admin')
    .eq('id', session.user.id)
    .single()
    .then(({ data: profile }) => {
      if (profile) {
        if (profile.name) {
          S.name = profile.name;
          setLs('vc_name', profile.name);
          const pillName = document.getElementById('user-pill-name');
          if (pillName) pillName.textContent = profile.name;
          if (tgGreet) tgGreet.textContent = `Hey ${profile.name}! What will you build today?`;
        }
        S.tier = profile.tier || 'free';
        S.isAdmin = profile.is_admin === true;
        if (S.isAdmin) S.tier = 'annual';
        const tierEl = document.getElementById('user-pill-tier');
        if (tierEl) tierEl.textContent = S.isAdmin ? 'ADMIN' : S.tier.toUpperCase();
        showFullStackButton();
      }
    })
    .catch(() => {});

  // Load usage in background
  const month = new Date().toISOString().slice(0, 7);
  sb.from('usage')
    .select('builds')
    .eq('user_id', session.user.id)
    .eq('month', month)
    .single()
    .then(({ data: usage }) => {
      S.builds = usage?.builds ?? 0;
    })
    .catch(() => {});

  // Playground starts fresh every time — users load previous projects from the drawer
  // (auto-save ensures nothing is lost)
  // BUT: load active conversation if one exists (long-term memory)
  loadActiveConversation();

  // Preload best examples
  preloadBestExamples();

  // Show Full-Stack button (will re-check after profile loads)
  showFullStackButton();

  // Welcome email for new users
  const welcomed = ls('kc_welcomed_' + session.user.id);
  if (!welcomed) {
    setLs('kc_welcomed_' + session.user.id, '1');
    fetch('/api/email/welcome', {
      method: 'POST',
      headers: { 'Authorization': 'Bearer ' + session.access_token },
    }).catch(() => {});
  }

  // Personalize
  const wm = document.querySelector('#welcome-msg .msg-bubble');
  if (wm && S.name) wm.innerHTML = wm.innerHTML.replace('Hey! 👋', `Hey ${S.name}! 👋`);

  // Check for pending build from Studio (user was redirected here with ?build= but wasn't signed in)
  const pendingBuild = sessionStorage.getItem('kc_pending_build');
  if (pendingBuild) {
    sessionStorage.removeItem('kc_pending_build');
    const input = document.getElementById('chat-input');
    if (input) input.value = pendingBuild;
    setTimeout(() => { if (window.send) window.send(); }, 800);
  }
}
